import type {
    GapStyleKey,
    MarginStyleKey,
    PaddingStyleKey,
    PositionStyleKey,
    RadiusStyleKey,
    StyleSheetKey,
    StyleSheetRecord,
    WidthStyleKey,
} from "@qendo/theme";
import { StyleSheet, type ViewStyle } from "react-native";
import { Radius, Space, Widths } from "~/constants/sizing";
import { makeStyleRecords } from "~/util/theme";

export type Spacing = typeof Space;

export type Roundness = typeof Radius;

export type Wideness = typeof Widths;

type SpaceStyleKey = GapStyleKey | PaddingStyleKey | MarginStyleKey | PositionStyleKey;

type SpacingStyleRecord<K extends SpaceStyleKey, T extends string = ""> = StyleSheetRecord<
    StyleSheetKey<keyof Spacing, T>,
    ViewStyle,
    K
>;
type WidthStyleRecord<K extends WidthStyleKey, T extends string = ""> = StyleSheetRecord<
    StyleSheetKey<keyof Wideness, T>,
    ViewStyle,
    K
>;
type RoundingStyleRecord<K extends RadiusStyleKey, T extends string = ""> = StyleSheetRecord<
    StyleSheetKey<keyof Roundness, T>,
    ViewStyle,
    K
>;

type GapRecord = SpacingStyleRecord<"gap">;
type ColumnGapRecord = SpacingStyleRecord<"columnGap", "column">;
type RowGapRecord = SpacingStyleRecord<"rowGap", "row">;

type GapStyles = GapRecord & ColumnGapRecord & RowGapRecord;

export const Gap = StyleSheet.create<GapStyles>({
    ...makeStyleRecords(Space, "gap"),
    ...makeStyleRecords(Space, "columnGap", "column"),
    ...makeStyleRecords(Space, "rowGap", "row"),
});

type PaddingRecord = SpacingStyleRecord<"padding">;
type PaddingTopRecord = SpacingStyleRecord<"paddingTop", "top">;
type PaddingRightRecord = SpacingStyleRecord<"paddingRight", "right">;
type PaddingBottomRecord = SpacingStyleRecord<"paddingBottom", "bottom">;
type PaddingLeftRecord = SpacingStyleRecord<"paddingLeft", "left">;
type PaddingHorizontalRecord = SpacingStyleRecord<"paddingHorizontal", "horizontal">;
type PaddingVerticalRecord = SpacingStyleRecord<"paddingVertical", "vertical">;

type PaddingStyles = PaddingRecord &
    PaddingTopRecord &
    PaddingRightRecord &
    PaddingBottomRecord &
    PaddingLeftRecord &
    PaddingHorizontalRecord &
    PaddingVerticalRecord;

export const Padding = StyleSheet.create<PaddingStyles>({
    ...makeStyleRecords(Space, "padding"),
    ...makeStyleRecords(Space, "paddingTop", "top"),
    ...makeStyleRecords(Space, "paddingRight", "right"),
    ...makeStyleRecords(Space, "paddingBottom", "bottom"),
    ...makeStyleRecords(Space, "paddingLeft", "left"),
    ...makeStyleRecords(Space, "paddingHorizontal", "horizontal"),
    ...makeStyleRecords(Space, "paddingVertical", "vertical"),
});

type MarginRecord = SpacingStyleRecord<"margin">;
type MarginTopRecord = SpacingStyleRecord<"marginTop", "top">;
type MarginRightRecord = SpacingStyleRecord<"marginRight", "right">;
type MarginBottomRecord = SpacingStyleRecord<"marginBottom", "bottom">;
type MarginLeftRecord = SpacingStyleRecord<"marginLeft", "left">;
type MarginHorizontalRecord = SpacingStyleRecord<"marginHorizontal", "horizontal">;
type MarginVerticalRecord = SpacingStyleRecord<"marginVertical", "vertical">;

type MarginStyles = MarginRecord &
    MarginTopRecord &
    MarginLeftRecord &
    MarginBottomRecord &
    MarginRightRecord &
    MarginHorizontalRecord &
    MarginVerticalRecord;

export const Margin = StyleSheet.create<MarginStyles>({
    ...makeStyleRecords(Space, "margin"),
    ...makeStyleRecords(Space, "marginTop", "top"),
    ...makeStyleRecords(Space, "marginRight", "right"),
    ...makeStyleRecords(Space, "marginBottom", "bottom"),
    ...makeStyleRecords(Space, "marginLeft", "left"),
    ...makeStyleRecords(Space, "marginHorizontal", "horizontal"),
    ...makeStyleRecords(Space, "marginVertical", "vertical"),
});

type RadiusRecord = RoundingStyleRecord<"borderRadius">;
type RadiusTopRecord = RoundingStyleRecord<"borderTopLeftRadius" | "borderTopRightRadius", "top">;
type RadiusRightRecord = RoundingStyleRecord<"borderTopRightRadius" | "borderBottomRightRadius", "right">;
type RadiusBottomRecord = RoundingStyleRecord<"borderBottomLeftRadius" | "borderBottomRightRadius", "bottom">;
type RadiusLeftRecord = RoundingStyleRecord<"borderTopLeftRadius" | "borderBottomLeftRadius", "left">;

type RadiusStyles = RadiusRecord & RadiusTopRecord & RadiusLeftRecord & RadiusBottomRecord & RadiusRightRecord;

export const Rounded = StyleSheet.create<RadiusStyles>({
    ...makeStyleRecords(Radius, "borderRadius"),
    ...makeStyleRecords(Radius, ["borderTopLeftRadius", "borderTopRightRadius"], "top"),
    ...makeStyleRecords(Radius, ["borderTopRightRadius", "borderBottomRightRadius"], "right"),
    ...makeStyleRecords(Radius, ["borderBottomLeftRadius", "borderBottomRightRadius"], "bottom"),
    ...makeStyleRecords(Radius, ["borderTopLeftRadius", "borderBottomLeftRadius"], "left"),
});

type WidthRecord = WidthStyleRecord<"width">;
type MaxWidthRecord = WidthStyleRecord<"maxWidth", "max">;
type MinWidthRecord = WidthStyleRecord<"minWidth", "min">;
type WidthStyles = WidthRecord & MaxWidthRecord & MinWidthRecord;

export const Width = StyleSheet.create<WidthStyles>({
    ...(makeStyleRecords(Widths, "width") as WidthRecord),
    ...(makeStyleRecords(Widths, "maxWidth", "max") as MaxWidthRecord),
    ...(makeStyleRecords(Widths, "minWidth", "min") as MinWidthRecord),
});

type Alignment = string & ViewStyle["alignItems"];
type AlignmentKey = "start" | "end" | "center" | "stretch" | "baseline";
type AlignmentRecord = StyleSheetRecord<AlignmentKey, ViewStyle, "alignItems", Alignment>;

export const Items = StyleSheet.create<AlignmentRecord>({
    start: { alignItems: "flex-start" },
    end: { alignItems: "flex-end" },
    center: { alignItems: "center" },
    stretch: { alignItems: "stretch" },
    baseline: { alignItems: "baseline" },
});
type Justify = string & ViewStyle["justifyContent"];
type JustifyKey = "start" | "end" | "center" | "between" | "around" | "evenly";
type JustifyRecord = Record<JustifyKey, { justifyContent: Justify }>;

type WrapKey = "wrap" | "nowrap" | "wrapReverse";
type WrapRecord = StyleSheetRecord<WrapKey, ViewStyle, "flexWrap">;

type Direction = string & ViewStyle["flexDirection"];
type DirectionKey = "row" | "column" | "rowReverse" | "columnReverse";
type DirectionRecord = StyleSheetRecord<DirectionKey, ViewStyle, "flexDirection", Direction>;

type FlexRecord = StyleSheetRecord<"flex" | "flex_0" | "flex_2", ViewStyle, "flex">;
type GrowRecord = StyleSheetRecord<"grow" | "grow_0", ViewStyle, "flexGrow">;
type ShrinkRecord = StyleSheetRecord<"shrink" | "shrink_0", ViewStyle, "flexShrink">;

type FlexStyles = JustifyRecord & DirectionRecord & FlexRecord & GrowRecord & ShrinkRecord & WrapRecord;

export const Flex = StyleSheet.create<FlexStyles>({
    start: { justifyContent: "flex-start" },
    end: { justifyContent: "flex-end" },
    center: { justifyContent: "center" },
    between: { justifyContent: "space-between" },
    around: { justifyContent: "space-around" },
    evenly: { justifyContent: "space-evenly" },
    row: { flexDirection: "row" },
    column: { flexDirection: "column" },
    rowReverse: { flexDirection: "row-reverse" },
    columnReverse: { flexDirection: "column-reverse" },
    wrap: { flexWrap: "wrap" },
    nowrap: { flexWrap: "nowrap" },
    wrapReverse: { flexWrap: "wrap-reverse" },
    flex: { flex: 1 },
    flex_0: { flex: 1 },
    flex_2: { flex: 2 },
    grow: { flexGrow: 1 },
    grow_0: { flexGrow: 0 },
    shrink: { flexShrink: 1 },
    shrink_0: { flexShrink: 0 },
});
